import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloneIcon from '@mui/icons-material/ContentCopy'

import { cloneScenario } from '../../../../actions'
import { Logger, LoggerTypes } from '../../../../utils'
import { AccessControl, AccessControlNoRights, Button, buttonModes } from '../../../../components'
import { userRightTypes } from '../../../../constants'

export const ScenarioDialogClone = forwardRef(({ pid, scenario, openDialog, setOpenDialog }, ref) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { t } = useTranslation()

  const refreshPage = () => {
    window.location.reload(false)
  }

  const onClickCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogClone.onClickCancel()')
    setOpenDialog(!openDialog)
  }

  const onClickClone = (e) => {
    e.preventDefault()
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogClone.onClickClone()', { pid, scenario })

    dispatch(cloneScenario(pid, scenario))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogClone.onClickClone().cloneScenario().then()', {
          scenario,
          res
        })

        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        }
        setOpenDialog(!openDialog)
        refreshPage()
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'ScenarioDialogClone.onClickClone().cloneScenario().catch()', { res })

        const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
        enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'ScenarioDialogClone.render()', { openDialog, pid, scenario })

  return (
    <Dialog ref={ref} open={openDialog} onClose={onClickCancel} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{`${t('scenarios.dialogs.clone.title')} ${scenario.name}`}</DialogTitle>
      <DialogContent>
        <AccessControl
          userPermissions={[userRightTypes.SCENARIO_EDIT]}
          renderNoAccess={() => <AccessControlNoRights />}
        >
          <DialogContentText>{t('scenarios.dialogs.clone.description')}</DialogContentText>
        </AccessControl>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onClickCancel}
        />
        <AccessControl userPermissions={[userRightTypes.SCENARIO_EDIT]} renderNoAccess={() => <></>}>
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            text={t('common.clone')}
            icon={<CloneIcon />}
            onClick={onClickClone}
          />
        </AccessControl>
      </DialogActions>
    </Dialog>
  )
})

ScenarioDialogClone.displayName = 'ScenarioDialogClone'

ScenarioDialogClone.propTypes = {
  pid: PropTypes.string.isRequired,
  scenario: PropTypes.object.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
}

ScenarioDialogClone.defaultProps = {
  pid: '1',
  scenario: {},
  openDialog: false,
  setOpenDialog: () => {}
}
